import { Component, OnInit } from '@angular/core'
import { AdmytService } from '../../../services/admyt.service'
import { Router } from '@angular/router'
import Swal from 'sweetalert2'

interface AuditRow {
    id: string
    status: 'synced' | 'parkupp_only' | 'admyt_only' | 'mismatch'
    expanded: boolean
    parkupp: any | null
    admyt: any | null
    children: AuditRow[]
    level: 'tenant' | 'user' | 'car'
    label: string
    centerId?: string
    actionLoading?: boolean
}

interface CenterAudit {
    centerId: string
    centerName: string
    expanded: boolean
    rows: AuditRow[]
    stats: { synced: number; parkuppOnly: number; admytOnly: number; mismatch: number }
}

@Component({
    selector: 'app-admin-admyt-audit',
    templateUrl: './admin-admyt-audit.component.html',
    styleUrls: ['./admin-admyt-audit.component.scss'],
})
export class AdminAdmytAuditComponent implements OnInit {
    loading = false
    loadingCenters = false
    error: string | null = null
    auditData: any = null
    centers: CenterAudit[] = []
    needsRefresh = false

    // Center picker
    availableCenters: Array<{ id: string; name: string }> = []
    selectedCenterId: string = ''
    showInactive: boolean = false

    constructor(private admytService: AdmytService, private router: Router) {}

    async ngOnInit() {
        await this.loadCenterList()
    }

    async loadCenterList() {
        this.loadingCenters = true
        try {
            const result = await this.admytService.getCentres()
            this.availableCenters = ((result.data as any) || []).map((c: any) => ({ id: String(c.id), name: c.name || c.id }))
        } catch {
            // Non-blocking
        } finally {
            this.loadingCenters = false
        }
    }

    async runAudit() {
        this.loading = true
        this.needsRefresh = false
        this.error = null
        this.auditData = null
        this.centers = []

        try {
            const result = await this.admytService.runAudit(this.selectedCenterId || undefined)
            this.auditData = result.data
            if (!this.auditData.success) {
                this.error = this.auditData.error || 'Audit failed'
            } else {
                this.buildComparisonRows()
            }
        } catch (err: any) {
            this.error = err.message || 'Failed to run audit'
        } finally {
            this.loading = false
        }
    }

    private buildComparisonRows() {
        if (!this.auditData?.centers) return

        for (const center of this.auditData.centers) {
            const ca: CenterAudit = {
                centerId: center.centerId,
                centerName: center.centerName,
                expanded: true,
                rows: [],
                stats: { synced: 0, parkuppOnly: 0, admytOnly: 0, mismatch: 0 },
            }

            const allSubs = center.parkuppParkings?.flatMap((p: any) => p.subscriptions) || []
            const activeSubs = allSubs.filter((s: any) => s.isActive)
            const allTenants = [...(center.admytTenants || [])]
            const matchedTenantIds = new Set<string>()

            for (const sub of activeSubs) {
                const matchedTenant = sub.matchedAdmytTenantId
                    ? allTenants.find((t: any) => t.id === sub.matchedAdmytTenantId)
                    : null

                if (matchedTenant) matchedTenantIds.add(matchedTenant.id)

                let status: 'synced' | 'parkupp_only' | 'admyt_only' | 'mismatch'
                if (!matchedTenant) {
                    status = 'parkupp_only'
                } else if (matchedTenant.status === 'inactive') {
                    status = 'mismatch'
                } else {
                    status = 'synced'
                }

                const row: AuditRow = {
                    id: sub.subscriptionId,
                    status,
                    expanded: status !== 'synced',
                    parkupp: sub,
                    admyt: matchedTenant || null,
                    children: this.buildUserRows(sub, matchedTenant, center.centerId),
                    level: 'tenant',
                    label: sub.clientName || sub.subscriptionId,
                    centerId: center.centerId,
                }

                ca.rows.push(row)
                ca.stats[status === 'synced' ? 'synced' : status === 'parkupp_only' ? 'parkuppOnly' : 'mismatch']++
            }

            for (const tenant of allTenants) {
                if (!matchedTenantIds.has(tenant.id) && tenant.status === 'active') {
                    const row: AuditRow = {
                        id: tenant.id,
                        status: 'admyt_only',
                        expanded: true,
                        parkupp: null,
                        admyt: tenant,
                        children: tenant.users?.map((u: any) => ({
                            id: u.id,
                            status: 'admyt_only' as const,
                            expanded: false,
                            parkupp: null,
                            admyt: u,
                            children: [],
                            level: 'user' as const,
                            label: `${u.firstName} ${u.lastName}`,
                            centerId: center.centerId,
                        })) || [],
                        level: 'tenant',
                        label: tenant.name,
                        centerId: center.centerId,
                    }
                    ca.rows.push(row)
                    ca.stats.admytOnly++
                }
            }

            this.centers.push(ca)
        }
    }

    private buildUserRows(sub: any, tenant: any, centerId: string): AuditRow[] {
        const rows: AuditRow[] = []
        const matchedAdmytUserIds = new Set<string>()

        for (const parker of sub.parkers || []) {
            const matchedUser = parker.matchedAdmytUserId && tenant
                ? tenant.users?.find((u: any) => u.id === parker.matchedAdmytUserId)
                : null

            if (matchedUser) matchedAdmytUserIds.add(matchedUser.id)

            rows.push({
                id: parker.parkerId,
                status: matchedUser ? 'synced' : 'parkupp_only',
                expanded: false,
                parkupp: parker,
                admyt: matchedUser || null,
                children: this.buildCarRows(parker, matchedUser),
                level: 'user',
                label: `${parker.firstName} ${parker.lastName}`,
                centerId,
            })
        }

        if (tenant) {
            for (const user of tenant.users || []) {
                if (!matchedAdmytUserIds.has(user.id)) {
                    rows.push({
                        id: user.id,
                        status: 'admyt_only',
                        expanded: false,
                        parkupp: null,
                        admyt: user,
                        children: [],
                        level: 'user',
                        label: `${user.firstName} ${user.lastName}`,
                        centerId,
                    })
                }
            }
        }

        return rows
    }

    private normalizePlate(plate: string): string {
        return (plate || '').toUpperCase().replace(/\s/g, '')
    }

    private buildCarRows(parker: any, admytUser: any): AuditRow[] {
        const rows: AuditRow[] = []
        const admytCars = admytUser?.cars || []

        for (const v of parker.vehicles || []) {
            const plate = this.normalizePlate(v.registration)
            const matchedCar = admytCars.find((c: any) => this.normalizePlate(c.plate) === plate)
            rows.push({
                id: v.vehicleId || plate, status: matchedCar ? 'synced' : 'parkupp_only',
                expanded: false, parkupp: v, admyt: matchedCar || null,
                children: [], level: 'car', label: plate || 'No plate',
            })
        }

        for (const car of admytCars) {
            const plate = this.normalizePlate(car.plate)
            const hasLocal = (parker.vehicles || []).some((v: any) => this.normalizePlate(v.registration) === plate)
            if (!hasLocal) {
                rows.push({
                    id: car.plate, status: 'admyt_only',
                    expanded: false, parkupp: null, admyt: car,
                    children: [], level: 'car', label: plate,
                })
            }
        }

        return rows
    }

    toggleRow(row: AuditRow) {
        row.expanded = !row.expanded
    }

    toggleCenter(center: CenterAudit) {
        center.expanded = !center.expanded
    }

    getVisibleRows(center: CenterAudit): AuditRow[] {
        if (this.showInactive) return center.rows
        return center.rows.filter((row) => {
            if (row.admyt?.status === 'inactive' && !row.parkupp?.isActive) return false
            return true
        })
    }

    getHiddenCount(center: CenterAudit): number {
        return center.rows.length - this.getVisibleRows(center).length
    }

    getVisibleChildren(row: AuditRow): AuditRow[] {
        if (this.showInactive) return row.children
        return row.children.filter((child) => {
            if (child.admyt && child.admyt.status && child.admyt.status !== 'active') return false
            return true
        })
    }

    getHiddenChildrenCount(row: AuditRow): number {
        return row.children.length - this.getVisibleChildren(row).length
    }

    async syncToParkUpp(row: AuditRow) {
        if (!row.parkupp?.clientId) return
        row.actionLoading = true
        try {
            const result = await this.admytService.resyncClientAdmyt(row.parkupp.clientId)
            const log: any[] = (result.data as any)?.log || []
            const errors = log.filter((l) => l.status === 'error')
            const skipped = log.filter((l) => l.status === 'skipped')
            const okCount = log.filter((l) => l.status === 'ok').length

            if (errors.length === 0) {
                await Swal.fire({ title: 'Sync Complete', text: `${okCount} step(s) succeeded.`, icon: 'success', timer: 2000, showConfirmButton: false })
            } else {
                const errorHtml = errors.map((e) => {
                    const stepLabel = e.step.replace(/^sub-\w+-/, '').replace(/-/g, ' ')
                    const detail = this.extractAdmytError(e.detail)
                    return `<div class="text-start mb-2"><strong>${stepLabel}</strong><br/><span class="text-danger">${detail}</span></div>`
                }).join('')

                const skippedHtml = skipped.length > 0
                    ? `<div class="text-muted text-start mt-2 small">${skipped.length} step(s) skipped</div>`
                    : ''

                await Swal.fire({
                    title: `Sync completed with ${errors.length} error(s)`,
                    html: `<div class="text-start small mb-2 text-muted">${okCount} step(s) succeeded</div>${errorHtml}${skippedHtml}`,
                    icon: 'warning',
                    width: 600,
                })
            }
            this.needsRefresh = true
        } catch (err: any) {
            Swal.fire({ title: 'Sync Failed', text: err.message, icon: 'error' })
        } finally {
            row.actionLoading = false
        }
    }

    private extractAdmytError(detail: string): string {
        const jsonMatch = detail.match(/\u2014\s*(\{.+\})$/)
        if (jsonMatch) {
            try {
                const parsed = JSON.parse(jsonMatch[1])
                return parsed.error || parsed.message || detail
            } catch { /* fall through */ }
        }
        return detail
    }

    async deleteFromAdmyt(row: AuditRow) {
        if (row.level === 'tenant' && row.admyt && row.centerId) {
            const confirmed = await Swal.fire({
                title: 'Deactivate Admyt Tenant?',
                text: `This will deactivate "${row.admyt.name}" and all its users will lose parking access.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, deactivate',
                confirmButtonColor: '#dc3545',
            })
            if (!confirmed.isConfirmed) return

            row.actionLoading = true
            try {
                await this.admytService.deactivateTenant(row.centerId, row.admyt.id)
                Swal.fire({ title: 'Deactivated', icon: 'success', timer: 1500, showConfirmButton: false })
                row.admyt.status = 'inactive'
                row.status = 'mismatch'
                this.needsRefresh = true
            } catch (err: any) {
                Swal.fire({ title: 'Failed', text: err.message, icon: 'error' })
            } finally {
                row.actionLoading = false
            }
        } else if (row.level === 'user' && row.admyt && row.centerId) {
            // Find the parent tenant ID
            const parentTenant = this.findParentTenant(row)
            if (!parentTenant) return

            const confirmed = await Swal.fire({
                title: 'Delete Admyt User?',
                text: `This will remove "${row.admyt.firstName} ${row.admyt.lastName}" from Admyt access control.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete',
                confirmButtonColor: '#dc3545',
            })
            if (!confirmed.isConfirmed) return

            row.actionLoading = true
            try {
                await this.admytService.deleteTenantUser(row.centerId, parentTenant, row.admyt.id)
                Swal.fire({ title: 'Deleted', icon: 'success', timer: 1500, showConfirmButton: false })
                this.removeChildRow(row)
                this.needsRefresh = true
            } catch (err: any) {
                Swal.fire({ title: 'Failed', text: err.message, icon: 'error' })
            } finally {
                row.actionLoading = false
            }
        }
    }

    private findParentTenant(userRow: AuditRow): string | null {
        for (const center of this.centers) {
            for (const tenantRow of center.rows) {
                if (tenantRow.children.some((c) => c.id === userRow.id)) {
                    return tenantRow.admyt?.id || null
                }
            }
        }
        return null
    }

    private removeChildRow(childRow: AuditRow) {
        for (const center of this.centers) {
            for (const tenantRow of center.rows) {
                const idx = tenantRow.children.indexOf(childRow)
                if (idx !== -1) {
                    tenantRow.children.splice(idx, 1)
                    return
                }
            }
        }
    }

    async deleteCarFromAdmyt(userRow: AuditRow, plate: string) {
        if (!userRow.admyt?.id) return

        const confirmed = await Swal.fire({
            title: 'Remove Car from Admyt?',
            text: `Remove plate "${plate}" from Admyt user ${userRow.admyt.firstName} ${userRow.admyt.lastName}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, remove',
            confirmButtonColor: '#dc3545',
        })
        if (!confirmed.isConfirmed) return

        try {
            await this.admytService.deleteUserCar(userRow.admyt.id, plate)
            Swal.fire({ title: 'Car Removed', icon: 'success', timer: 1500, showConfirmButton: false })
            userRow.admyt.cars = (userRow.admyt.cars || []).filter((c: any) => c.plate !== plate)
            this.needsRefresh = true
        } catch (err: any) {
            Swal.fire({ title: 'Failed', text: err.message, icon: 'error' })
        }
    }
}
